const accessTokenStorageKey = 'ca_access_token';

export { accessTokenStorageKey };

let refreshPromise = null;
let onTokenRefreshed = null;

export function setTokenRefreshCallback(callback) {
  onTokenRefreshed = callback;
}

async function tryRefreshToken() {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    try {
      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        credentials: 'include'
      });

      if (!response.ok) {
        return null;
      }

      const payload = await response.json();
      if (payload.accessToken) {
        localStorage.setItem(accessTokenStorageKey, payload.accessToken);
        if (onTokenRefreshed) {
          onTokenRefreshed(payload.accessToken);
        }
        return payload.accessToken;
      }

      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

async function authenticatedFetch(url, options = {}) {
  const response = await fetch(url, options);

  if (response.status !== 401) {
    return response;
  }

  const newToken = await tryRefreshToken();
  if (!newToken) {
    return response;
  }

  const retryOptions = {
    ...options,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${newToken}`
    }
  };

  return fetch(url, retryOptions);
}

export async function restoreSessionFromApi(signal) {
  const storedToken = localStorage.getItem(accessTokenStorageKey);

  if (storedToken) {
    const profileResponse = await fetch('/api/auth/me', {
      headers: {
        Authorization: `Bearer ${storedToken}`
      },
      signal
    });

    if (profileResponse.ok) {
      return {
        accessToken: storedToken,
        user: await profileResponse.json()
      };
    }
  }

  const refreshResponse = await fetch('/api/auth/refresh', {
    method: 'POST',
    credentials: 'include',
    signal
  });

  if (!refreshResponse.ok) {
    return null;
  }

  return refreshResponse.json();
}

export async function loginToApi(credentials, signal) {
  const response = await fetch('/api/auth/login', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(credentials),
    signal
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(payload.message || 'Unable to sign in.');
    error.status = response.status;
    throw error;
  }

  return payload;
}

export async function requestMagicLinkApi(email, signal) {
  const response = await fetch('/api/auth/magic-link', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
    signal
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload.message || 'Unable to send magic link.');
  }

  return payload;
}

export async function verifyMagicLinkApi(token, signal) {
  const response = await fetch('/api/auth/verify-magic-link', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ token }),
    signal
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload.message || 'Magic link is invalid or expired.');
  }

  return payload;
}

export async function logoutFromApi(signal) {
  await fetch('/api/auth/logout', {
    method: 'POST',
    credentials: 'include',
    signal
  });
}

export async function requestProjects(accessToken, signal) {
  const response = await authenticatedFetch('/api/projects', {
    headers: {
      Authorization: `Bearer ${accessToken}`
    },
    signal
  });

  if (!response.ok) {
    throw createApiError(response.status, 'Unable to load projects.');
  }

  return response.json();
}

export async function createProject(accessToken, payload, signal) {
  const response = await authenticatedFetch('/api/projects', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload),
    signal
  });

  const parsedPayload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(parsedPayload.message || 'Unable to create the project.');
    error.status = response.status;
    throw error;
  }

  return parsedPayload;
}

export async function deleteProject(accessToken, projectId, signal) {
  const response = await authenticatedFetch(`/api/projects/${projectId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${accessToken}` },
    signal
  });

  const parsed = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(parsed.message || 'Unable to delete the project.');
    error.status = response.status;
    throw error;
  }
  return parsed;
}

export async function updateProjectSettings(accessToken, projectId, settings, signal) {
  const response = await authenticatedFetch(`/api/projects/${projectId}/settings`, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(settings),
    signal
  });

  const parsedPayload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(parsedPayload.message || 'Unable to update project settings.');
    error.status = response.status;
    throw error;
  }

  return parsedPayload.settings;
}

export async function requestFactors(accessToken, projectId, signal) {
  const response = await authenticatedFetch(`/api/factors/${projectId}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
    signal
  });

  if (!response.ok) {
    throw createApiError(response.status, 'Unable to load factor definitions.');
  }

  return response.json();
}

export async function createFactorDefinition(accessToken, projectId, payload, signal) {
  const response = await authenticatedFetch(`/api/factors/${projectId}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload),
    signal
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data.message || 'Unable to create the factor.');
    error.status = response.status;
    throw error;
  }

  return data;
}

export async function updateFactorDefinition(accessToken, projectId, definitionId, payload, signal) {
  const response = await authenticatedFetch(`/api/factors/${projectId}/${definitionId}`, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload),
    signal
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data.message || 'Unable to update the factor.');
    error.status = response.status;
    throw error;
  }

  return data;
}

export async function updateFactorSetting(accessToken, projectId, settingId, payload, signal) {
  const response = await authenticatedFetch(`/api/factors/${projectId}/settings/${settingId}`, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload),
    signal
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data.message || 'Unable to update the factor setting.');
    error.status = response.status;
    throw error;
  }

  return data;
}

export async function deleteFactorDefinition(accessToken, projectId, definitionId, signal) {
  const response = await authenticatedFetch(`/api/factors/${projectId}/${definitionId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${accessToken}` },
    signal
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    const error = new Error(data.message || 'Unable to delete the factor.');
    error.status = response.status;
    throw error;
  }
}

export async function requestDashboard(accessToken, projectId, signal) {
  const queryString = projectId ? `?projectId=${projectId}` : '';
  const response = await authenticatedFetch(`/api/dashboard${queryString}`, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    },
    signal
  });

  if (!response.ok) {
    throw createApiError(response.status, 'Unable to load the placement dashboard.');
  }

  return response.json();
}

export async function requestRoster(accessToken, projectId, signal) {
  const response = await authenticatedFetch(`/api/roster/${projectId}`, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    },
    signal
  });

  if (!response.ok) {
    throw createApiError(response.status, 'Unable to load the students workspace.');
  }

  return response.json();
}

export async function requestClasses(accessToken, projectId, signal) {
  const response = await authenticatedFetch(`/api/classes/${projectId}`, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    },
    signal
  });

  if (!response.ok) {
    throw createApiError(response.status, 'Unable to load the classes workspace.');
  }

  return response.json();
}

export async function requestSavedRuns(accessToken, projectId, runId, signal) {
  const queryString = new URLSearchParams();
  if (runId) {
    queryString.set('runId', runId);
  }

  const response = await authenticatedFetch(
    `/api/runs/${projectId}${queryString.toString() ? `?${queryString.toString()}` : ''}`,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`
      },
      signal
    }
  );

  if (!response.ok) {
    throw createApiError(response.status, 'Unable to load saved runs.');
  }

  return response.json();
}

export async function renameRun(accessToken, projectId, runId, payload, signal) {
  const response = await authenticatedFetch(`/api/runs/${projectId}/${runId}`, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload),
    signal
  });

  const parsedPayload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(parsedPayload.message || 'Unable to rename the run.');
    error.status = response.status;
    throw error;
  }

  return parsedPayload;
}

export async function deleteRun(accessToken, projectId, runId, signal) {
  const response = await authenticatedFetch(`/api/runs/${projectId}/${runId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${accessToken}` },
    signal
  });

  const parsedPayload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(parsedPayload.message || 'Unable to delete the run.');
    error.status = response.status;
    throw error;
  }

  return parsedPayload;
}

export async function updateRosterStudent(accessToken, projectId, studentId, payload, signal) {
  const response = await authenticatedFetch(`/api/roster/${projectId}/students/${studentId}`, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload),
    signal
  });

  const parsedPayload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(parsedPayload.message || 'Unable to save student changes.');
    error.status = response.status;
    throw error;
  }

  return parsedPayload;
}

export async function createRosterStudent(accessToken, projectId, payload, signal) {
  const response = await authenticatedFetch(`/api/roster/${projectId}/students`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload),
    signal
  });

  const parsedPayload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(parsedPayload.message || 'Unable to create student.');
    error.status = response.status;
    throw error;
  }

  return parsedPayload;
}

export async function deleteRosterStudent(accessToken, projectId, studentId, signal) {
  const response = await authenticatedFetch(`/api/roster/${projectId}/students/${studentId}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${accessToken}`
    },
    signal
  });

  const parsedPayload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(parsedPayload.message || 'Unable to delete student.');
    error.status = response.status;
    throw error;
  }

  return parsedPayload;
}

export async function importRoster(accessToken, projectId, fileContent, fileName, mode, signal) {
  const response = await authenticatedFetch(`/api/roster/${projectId}/import`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ fileContent, fileName, mode }),
    signal
  });

  const parsedPayload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(parsedPayload.message || 'Unable to import roster.');
    error.status = response.status;
    throw error;
  }

  return parsedPayload;
}

export async function updateStudentRelationships(
  accessToken,
  projectId,
  studentId,
  relationshipType,
  payload,
  signal
) {
  const response = await authenticatedFetch(
    `/api/roster/${projectId}/students/${studentId}/relationships/${relationshipType}`,
    {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload),
      signal
    }
  );

  const parsedPayload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(parsedPayload.message || 'Unable to save relationship changes.');
    error.status = response.status;
    throw error;
  }

  return parsedPayload;
}

export async function createProjectClass(accessToken, projectId, payload, signal) {
  const response = await authenticatedFetch(`/api/classes/${projectId}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload),
    signal
  });

  const parsedPayload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(parsedPayload.message || 'Unable to create the class.');
    error.status = response.status;
    throw error;
  }

  return parsedPayload;
}

export async function updateProjectClass(accessToken, projectId, classId, payload, signal) {
  const response = await authenticatedFetch(`/api/classes/${projectId}/${classId}`, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload),
    signal
  });

  const parsedPayload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(parsedPayload.message || 'Unable to save class changes.');
    error.status = response.status;
    throw error;
  }

  return parsedPayload;
}

export async function deleteProjectClass(accessToken, projectId, classId, signal) {
  const response = await authenticatedFetch(`/api/classes/${projectId}/${classId}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${accessToken}`
    },
    signal
  });

  const parsedPayload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(parsedPayload.message || 'Unable to delete the class.');
    error.status = response.status;
    throw error;
  }

  return parsedPayload;
}

export async function requestGrades(accessToken, projectId, signal) {
  const response = await authenticatedFetch(`/api/grades/${projectId}`, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    },
    signal
  });

  if (!response.ok) {
    throw createApiError(response.status, 'Unable to load the grades workspace.');
  }

  return response.json();
}

export async function createProjectGrade(accessToken, projectId, payload, signal) {
  const response = await authenticatedFetch(`/api/grades/${projectId}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload),
    signal
  });

  const parsedPayload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(parsedPayload.message || 'Unable to create the grade.');
    error.status = response.status;
    throw error;
  }

  return parsedPayload;
}

export async function updateProjectGrade(accessToken, projectId, gradeId, payload, signal) {
  const response = await authenticatedFetch(`/api/grades/${projectId}/${gradeId}`, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload),
    signal
  });

  const parsedPayload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(parsedPayload.message || 'Unable to save grade changes.');
    error.status = response.status;
    throw error;
  }

  return parsedPayload;
}

export async function deleteProjectGrade(accessToken, projectId, gradeId, signal) {
  const response = await authenticatedFetch(`/api/grades/${projectId}/${gradeId}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${accessToken}`
    },
    signal
  });

  const parsedPayload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(parsedPayload.message || 'Unable to delete the grade.');
    error.status = response.status;
    throw error;
  }

  return parsedPayload;
}

export async function generateProposal(accessToken, projectId, payload = {}, signal) {
  const queryString = projectId ? `?projectId=${projectId}` : '';
  const response = await authenticatedFetch(`/api/dashboard/generate${queryString}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload),
    signal
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    const error = new Error(
      response.status === 401
        ? 'Your session expired. Sign in again to generate a proposal.'
        : payload.message || 'Unable to generate a new proposal.'
    );
    error.status = response.status;
    throw error;
  }

  return response.json();
}

export async function requestUsers(accessToken, schoolId, signal) {
  const response = await authenticatedFetch(`/api/users?schoolId=${schoolId}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
    signal
  });

  if (!response.ok) {
    throw createApiError(response.status, 'Unable to load users.');
  }

  return response.json();
}

export async function createUserApi(accessToken, payload, signal) {
  const response = await authenticatedFetch('/api/users', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload),
    signal
  });

  const parsedPayload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(parsedPayload.message || 'Unable to create user.');
    error.status = response.status;
    throw error;
  }

  return parsedPayload;
}

export async function updateUserApi(accessToken, userId, payload, signal) {
  const response = await authenticatedFetch(`/api/users/${userId}`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload),
    signal
  });

  const parsedPayload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(parsedPayload.message || 'Unable to update user.');
    error.status = response.status;
    throw error;
  }

  return parsedPayload;
}

export async function deleteUserApi(accessToken, userId, schoolId, signal) {
  const response = await authenticatedFetch(`/api/users/${userId}?schoolId=${schoolId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${accessToken}` },
    signal
  });

  const parsedPayload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(parsedPayload.message || 'Unable to remove user.');
    error.status = response.status;
    throw error;
  }

  return parsedPayload;
}

export async function changePasswordApi(accessToken, currentPassword, newPassword, signal) {
  const response = await authenticatedFetch('/api/auth/change-password', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ currentPassword, newPassword }),
    signal
  });

  const parsedPayload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(parsedPayload.message || 'Unable to change password.');
    error.status = response.status;
    throw error;
  }

  return parsedPayload;
}

export async function markTutorialSeenApi(accessToken, signal) {
  const response = await authenticatedFetch('/api/auth/mark-tutorial-seen', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    },
    body: '{}',
    signal
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    const error = new Error(payload.message || 'Unable to update tutorial status.');
    error.status = response.status;
    throw error;
  }
  return response.json();
}

// --- Platform Admin API ---

export async function requestAdminSchools(accessToken, signal) {
  const response = await authenticatedFetch('/api/admin/schools', {
    headers: { Authorization: `Bearer ${accessToken}` },
    signal
  });

  if (!response.ok) {
    throw createApiError(response.status, 'Unable to load schools.');
  }

  return response.json();
}

export async function createAdminSchool(accessToken, payload, signal) {
  const response = await authenticatedFetch('/api/admin/schools', {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal
  });

  const parsed = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(parsed.message || 'Unable to create school.');
    error.status = response.status;
    throw error;
  }
  return parsed;
}

export async function updateAdminSchool(accessToken, schoolId, payload, signal) {
  const response = await authenticatedFetch(`/api/admin/schools/${schoolId}`, {
    method: 'PUT',
    headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal
  });

  const parsed = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(parsed.message || 'Unable to update school.');
    error.status = response.status;
    throw error;
  }
  return parsed;
}

export async function deleteAdminSchool(accessToken, schoolId, signal) {
  const response = await authenticatedFetch(`/api/admin/schools/${schoolId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${accessToken}` },
    signal
  });

  const parsed = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(parsed.message || 'Unable to delete school.');
    error.status = response.status;
    throw error;
  }
  return parsed;
}

export async function reseedAdminSchool(accessToken, schoolId, signal) {
  const response = await authenticatedFetch(`/api/admin/schools/${schoolId}/reseed`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}` },
    signal
  });

  const parsed = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(parsed.message || 'Unable to reseed demo data.');
    error.status = response.status;
    throw error;
  }
  return parsed;
}

export async function requestAdminUsers(accessToken, signal) {
  const response = await authenticatedFetch('/api/admin/users', {
    headers: { Authorization: `Bearer ${accessToken}` },
    signal
  });

  if (!response.ok) {
    throw createApiError(response.status, 'Unable to load users.');
  }

  return response.json();
}

export async function updateAdminUserRole(accessToken, userId, role, signal) {
  const response = await authenticatedFetch(`/api/admin/users/${userId}/role`, {
    method: 'PUT',
    headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ role }),
    signal
  });

  const parsed = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(parsed.message || 'Unable to update user role.');
    error.status = response.status;
    throw error;
  }
  return parsed;
}

export async function resetAdminUserPassword(accessToken, userId, tempPassword, signal) {
  const response = await authenticatedFetch(`/api/admin/users/${userId}/reset-password`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ tempPassword }),
    signal
  });

  const parsed = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(parsed.message || 'Unable to reset password.');
    error.status = response.status;
    throw error;
  }
  return parsed;
}

export async function assignAdminUserToSchool(accessToken, userId, payload, signal) {
  const response = await authenticatedFetch(`/api/admin/users/${userId}/assign-school`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal
  });

  const parsed = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(parsed.message || 'Unable to assign user to school.');
    error.status = response.status;
    throw error;
  }
  return parsed;
}

export async function removeAdminUserFromSchool(accessToken, userId, schoolId, signal) {
  const response = await authenticatedFetch(`/api/admin/users/${userId}/remove-school`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ schoolId }),
    signal
  });

  const parsed = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(parsed.message || 'Unable to remove user from school.');
    error.status = response.status;
    throw error;
  }
  return parsed;
}

export async function createAdminUserApi(accessToken, payload, signal) {
  const response = await authenticatedFetch('/api/admin/users', {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal
  });

  const parsed = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(parsed.message || 'Unable to create user.');
    error.status = response.status;
    throw error;
  }
  return parsed;
}

export async function updateSchoolUserRoleApi(accessToken, userId, schoolId, role, signal) {
  const response = await authenticatedFetch(`/api/admin/users/${userId}/school-role`, {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ schoolId, role }),
    signal
  });

  const parsed = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(parsed.message || 'Unable to update school user role.');
    error.status = response.status;
    throw error;
  }
  return parsed;
}

export async function moveAdminUserToSchool(accessToken, userId, payload, signal) {
  const response = await authenticatedFetch(`/api/admin/users/${userId}/move-school`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal
  });

  const parsed = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(parsed.message || 'Unable to move user to school.');
    error.status = response.status;
    throw error;
  }
  return parsed;
}

export async function assignStudentApi(accessToken, projectId, studentId, classId, signal) {
  const response = await authenticatedFetch(
    `/api/dashboard/students/${studentId}/assignment?projectId=${projectId}`,
    {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ classId }),
      signal
    }
  );

  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw createApiError(response.status, payload.message || 'Unable to assign student.');
  }

  return response.json();
}

export async function toggleStudentLockApi(accessToken, projectId, studentId, isLocked, signal) {
  const response = await authenticatedFetch(
    `/api/dashboard/students/${studentId}/lock?projectId=${projectId}`,
    {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ isLocked }),
      signal
    }
  );

  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw createApiError(response.status, payload.message || 'Unable to update lock status.');
  }

  return response.json();
}

export async function toggleStudentBehaviorApi(accessToken, projectId, studentId, behaviorFlag, signal) {
  const response = await authenticatedFetch(
    `/api/dashboard/students/${studentId}/behavior?projectId=${projectId}`,
    {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ behaviorFlag }),
      signal
    }
  );

  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw createApiError(response.status, payload.message || 'Unable to update behavior status.');
  }

  return response.json();
}

export async function applyRunAssignmentsApi(accessToken, projectId, runId, studentIds, signal) {
  const response = await authenticatedFetch(
    `/api/dashboard/apply-assignments?projectId=${projectId}`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ runId, studentIds }),
      signal
    }
  );

  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw createApiError(response.status, payload.message || 'Unable to apply assignments.');
  }

  return response.json();
}

export async function sendChatMessage(accessToken, messages, schoolType, signal) {
  const response = await authenticatedFetch('/api/chat', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ messages, schoolType }),
    signal
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw createApiError(response.status, payload.message || 'Unable to reach the assistant.');
  }

  return response.json();
}

function createApiError(status, fallbackMessage) {
  const error = new Error(
    status === 401 ? 'Your session expired. Sign in again to continue.' : fallbackMessage
  );
  error.status = status;
  return error;
}
